import {
  INCOME_PER_PROPERTY,
  type PlayerBuildingCounts,
  type PlayerId,
} from "@dailywars/core/engine";
import type { ReactNode } from "react";

import { Infotip, InfotipContent, InfotipProvider, InfotipTrigger } from "#/components/ui/infotip";
import { PLAYER_COLORS } from "#/game/constants";

import { CoinMoneyIcon } from "./CoinMoneyIcon";

interface GameHUDProps {
  dayNumber: number;
  funds: Record<PlayerId, number>;
  buildingCounts: PlayerBuildingCounts;
}

function HudInfotipStat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Infotip>
      <InfotipTrigger aria-label={label} render={<span className="inline-flex cursor-default" />}>
        {children}
      </InfotipTrigger>
      <InfotipContent side="bottom">{label}</InfotipContent>
    </Infotip>
  );
}

function HudStackedStat({ icon, label }: { icon: ReactNode; label: ReactNode }) {
  return (
    <span className="inline-flex flex-col items-center gap-0.5">
      <span className="inline-flex">{icon}</span>
      <span className="inline-flex tabular-nums">{label}</span>
    </span>
  );
}

function dailyIncome(counts: PlayerBuildingCounts[PlayerId]): number {
  return (
    (counts.barracks + counts.factories + counts.bases + counts.cities + counts.airports) *
    INCOME_PER_PROPERTY
  );
}

function PlayerFundsChip({
  playerId,
  funds,
  income,
}: {
  playerId: PlayerId;
  funds: number;
  income: number;
}) {
  const incomeLabel = income > 0 ? `+${income}/day income` : "No income";
  return (
    <span
      className="flex self-stretch items-center px-2 py-2 text-white"
      style={{ backgroundColor: PLAYER_COLORS[playerId] }}
    >
      <HudInfotipStat label={incomeLabel}>
        <HudStackedStat
          icon={
            <span className="inline-flex">
              <CoinMoneyIcon size="sm" />
            </span>
          }
          label={<span className="inline-block min-w-[4ch] text-center text-xs">{funds}</span>}
        />
      </HudInfotipStat>
    </span>
  );
}

function PlayerBar({
  playerId,
  funds,
  income,
  side,
}: {
  playerId: PlayerId;
  funds: number;
  income: number;
  side: "left" | "right";
}) {
  const justify = side === "left" ? "justify-start" : "justify-end";
  const towardDayPad = side === "left" ? "pr-2 sm:pr-3" : "pl-2 sm:pl-3";
  return (
    <span
      className={`flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-1 tabular-nums sm:gap-x-3 ${justify} ${towardDayPad}`}
    >
      <PlayerFundsChip playerId={playerId} funds={funds} income={income} />
    </span>
  );
}

export function GameHUD({ dayNumber, funds, buildingCounts }: GameHUDProps) {
  return (
    <InfotipProvider delay={300}>
      <div className="flex items-stretch text-sm font-semibold text-foreground">
        <PlayerBar
          playerId="p1"
          funds={funds.p1}
          income={dailyIncome(buildingCounts.p1)}
          side="left"
        />
        <span className="flex shrink-0 items-center px-2 sm:px-3">Day {dayNumber}</span>
        <PlayerBar
          playerId="p2"
          funds={funds.p2}
          income={dailyIncome(buildingCounts.p2)}
          side="right"
        />
      </div>
    </InfotipProvider>
  );
}
